import {FC} from 'react'
import {CrewOvertime} from '../core/_requests'

type Props = {
  visible: boolean
  overtime: CrewOvertime | null
  onClose: () => void
}

const formatCurrency = (value: number) =>
  Number(value || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})

const resolveCurrencyCode = (salaryCurrency?: string | null, salaryOtherCurrency?: string | null) => {
  const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
  const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
  if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
  return normalizedAccountCurrency
}

const getCurrencySymbol = (currencyCode?: string) => {
  const code = (currencyCode || '').toString().trim().toUpperCase()
  switch (code) {
    case 'USD':
      return '$'
    case 'EUR':
      return '\u20AC'
    case 'GBP':
      return '\u00A3'
    case 'SGD':
      return 'S$'
    case 'INR':
      return '\u20B9'
    case 'AED':
      return '\u062F.\u0625 '
    default:
      try {
        const part = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).formatToParts(0).find((p) => p.type === 'currency')
        const maybeSymbol = (part?.value || '').trim()
        return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
      } catch {
        return ''
      }
  }
}

const statusBadge = (status: string) => {
  if (status === 'DRAFT') return 'badge-light-secondary'
  if (status === 'SUBMITTED') return 'badge-light-warning'
  if (status === 'REVIEWED') return 'badge-light-success'
  if (status === 'REJECTED') return 'badge-light-danger'
  return 'badge-light'
}

const statusLabel = (status: string) => (status === 'REJECTED' ? 'REVERTED' : status)

const ViewCrewOvertimeModal: FC<Props> = ({visible, overtime, onClose}) => {
  if (!visible || !overtime) return null

  const currencySymbol = getCurrencySymbol(resolveCurrencyCode(overtime.salaryCurrency, overtime.salaryOtherCurrency))

  return (
    <div className='modal-overlay' onClick={onClose}>
      <div className='modal-content' onClick={(e) => e.stopPropagation()} style={{maxWidth: '900px'}}>
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>Crew Overtime Details</h5>
          <button className='btn-close' onClick={onClose} />
        </div>

        <div className='custom-modal-body'>
          <div className='row g-3'>
            <div className='col-md-4'><div className='text-muted fs-7'>Crew</div><div className='fw-semibold'>{overtime.crewName || '-'}</div></div>
            <div className='col-md-4'><div className='text-muted fs-7'>Rank</div><div className='fw-semibold'>{overtime.rankName || '-'}</div></div>
            <div className='col-md-4'><div className='text-muted fs-7'>Month</div><div className='fw-semibold'>{overtime.overtimeMonth?.slice(0, 7) || '-'}</div></div>
            <div className='col-md-4'><div className='text-muted fs-7'>OT Hours</div><div className='fw-semibold'>{overtime.otHours || 0}</div></div>
            <div className='col-md-4'><div className='text-muted fs-7'>Fixed OT</div><div className='fw-semibold'>{overtime.maxOtAllowed || 0}</div></div>

            <div className='col-md-4'><div className='text-muted fs-7'>Overtime Rate</div><div className='fw-semibold'>{`${currencySymbol}${formatCurrency(overtime.overtimeRate ?? overtime.otPerHour ?? 0)}`}</div></div>
            <div className='col-md-4'><div className='text-muted fs-7'>OT Per Month</div><div className='fw-semibold'>{`${currencySymbol}${formatCurrency(overtime.otPerMonth || 0)}`}</div></div>
            <div className='col-md-4'><div className='text-muted fs-7'>OT Per Hour</div><div className='fw-semibold'>{`${currencySymbol}${formatCurrency(overtime.otPerHour || 0)}`}</div></div>

            <div className='col-md-4'><div className='text-muted fs-7'>OT Amount</div><div className='fw-semibold'>{`${currencySymbol}${formatCurrency(overtime.otAmount || 0)}`}</div></div>

            <div className='col-md-4'>
              <div className='text-muted fs-7'>Status</div>
              <span className={`badge ${statusBadge(overtime.status)}`}>{statusLabel(overtime.status)}</span>
            </div>
            <div className='col-md-8'><div className='text-muted fs-7'>Reviewed By</div><div className='fw-semibold'>{overtime.reviewedByName || '-'}</div></div>

            <div className='col-md-12'><div className='text-muted fs-7'>Remarks</div><div className='border rounded p-2'>{overtime.remarks || '-'}</div></div>
            <div className='col-md-12'><div className='text-muted fs-7'>Office Remarks</div><div className='border rounded p-2'>{overtime.officeRemarks || '-'}</div></div>
            {overtime.rejectionReason ? (
              <div className='col-md-12'><div className='text-muted fs-7'>Revert Reason</div><div className='border rounded p-2 bg-light-danger'>{overtime.rejectionReason}</div></div>
            ) : null}
          </div>
        </div>

        <div className='custom-modal-footer'>
          <button type='button' className='btn btn-light' onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

export default ViewCrewOvertimeModal
